import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePortalPatientData } from './portalContext';
import { Panel, PortalPage } from './ui';
import { SUPPORTED_LANGUAGES, setPortalLanguage, type SupportedLanguage } from '../../i18n';

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { contexts, profile } = usePortalPatientData();

  const currentLanguage = (i18n.language?.startsWith('es') ? 'es' : 'en') as SupportedLanguage;
  const [language, setLanguage] = useState<SupportedLanguage>(currentLanguage);

  const onLanguageChange = (next: SupportedLanguage) => {
    setLanguage(next);
    setPortalLanguage(next);
  };

  const languageLabel = (value: SupportedLanguage) =>
    value === 'es' ? t('settings.languageEs') : t('settings.languageEn');

  return (
    <PortalPage title={t('settings.title')} description={t('settings.description')}>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel title={t('settings.account')}>
          <div className="space-y-2 text-sm">
            <p>
              <span className="text-slate-500">{t('settings.displayName')}</span> {profile?.firstName} {profile?.lastName}
            </p>
            <p>
              <span className="text-slate-500">{t('settings.email')}</span> {profile?.email || t('common.na')}
            </p>
            <p>
              <span className="text-slate-500">{t('settings.defaultProvider')}</span>{' '}
              {profile?.provider?.name || t('common.notAssigned')}
            </p>
          </div>
        </Panel>

        <Panel title={t('settings.language')}>
          <div className="space-y-3 text-sm">
            <p className="text-slate-500">{t('settings.languageHint')}</p>
            <select
              value={language}
              onChange={(event) => onLanguageChange(event.target.value as SupportedLanguage)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
            >
              {SUPPORTED_LANGUAGES.map((value) => (
                <option key={value} value={value}>
                  {languageLabel(value)}
                </option>
              ))}
            </select>
          </div>
        </Panel>

        <Panel title={t('settings.contextAccess')}>
          <div className="space-y-2 text-sm">
            {contexts.map((context) => (
              <div key={context.patientId} className="rounded-lg border border-slate-100 p-3">
                <p className="font-medium text-slate-700">
                  {context.relationship === 'SELF' ? t('common.me') : context.relationship} · {context.firstName}{' '}
                  {context.lastName}
                </p>
                <p className="text-slate-500">
                  {t('settings.contextId')} {context.patientId}
                </p>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </PortalPage>
  );
}
